import React, { useState } from 'react';

const InfluencerModal = ({ influencer, onClose }) => {
  const [activeTab, setActiveTab] = useState('personal');

  const renderSocialMedia = () => {
    if (!influencer.socialMedia) {
      return (
        <div className="text-center text-gray-500 py-8">
          <i className="bi bi-share text-4xl mb-2"></i>
          <br />
          No social media information available
        </div>
      );
    }

    const socialPlatforms = Object.keys(influencer.socialMedia);
    const hasSocialData = socialPlatforms.some(platform => {
      const social = influencer.socialMedia[platform];
      return social && (social.profileUrl || social.followers || social.subscribers);
    });

    if (!hasSocialData) {
      return (
        <div className="text-center text-gray-500 py-8">
          <i className="bi bi-share text-4xl mb-2"></i>
          <br />
          No social media information available
        </div>
      );
    }

    const platformIcons = {
      'instagram': 'bi bi-instagram text-pink-500',
      'youtube': 'bi bi-youtube text-red-500',
      'tiktok': 'bi bi-tiktok text-black',
      'snapchat': 'bi bi-snapchat text-yellow-500',
      'facebook': 'bi bi-facebook text-blue-500',
      'blog': 'bi bi-journal-text text-gray-500'
    };

    return (
      <div className="space-y-4">
        {socialPlatforms.map(platform => {
          const social = influencer.socialMedia[platform];
          if (!social || (!social.profileUrl && !social.followers && !social.subscribers)) return null;

          return (
            <div key={platform} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <i className={`${platformIcons[platform] || 'bi bi-link'} text-2xl mr-3`}></i>
                <div className="flex-grow">
                  <h6 className="font-semibold mb-0">{platform.charAt(0).toUpperCase() + platform.slice(1)}</h6>
                  {social.profileUrl && (
                    <a href={social.profileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-sm hover:underline">
                      {social.profileUrl}
                    </a>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                {social.followers && <div><strong>Followers:</strong> {social.followers.toLocaleString()}</div>}
                {social.subscribers && <div><strong>Subscribers:</strong> {social.subscribers.toLocaleString()}</div>}
                {social.pricePerPost && <div><strong>Post:</strong> ₹{social.pricePerPost}</div>}
                {social.pricePerReel && <div><strong>Reel:</strong> ₹{social.pricePerReel}</div>}
                {social.pricePerStory && <div><strong>Story:</strong> ₹{social.pricePerStory}</div>}
                {social.pricePerVideo && <div><strong>Video:</strong> ₹{social.pricePerVideo}</div>}
                {social.pricePerLiveStream && <div><strong>Live Stream:</strong> ₹{social.pricePerLiveStream}</div>}
                {social.pricePerBlogPost && <div><strong>Blog Post:</strong> ₹{social.pricePerBlogPost}</div>}
                {social.pricePerBlogAd && <div><strong>Blog Ad:</strong> ₹{social.pricePerBlogAd}</div>}
                {social.combo && <div><strong>Combo:</strong> ₹{social.combo}</div>}
                {social.carouselPrice && <div><strong>Carousel:</strong> ₹{social.carouselPrice}</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold flex items-center">
            <i className="bi bi-person-circle mr-2"></i>
            Influencer Profile
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Profile Header */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 mx-auto mb-3">
              {influencer.image ? (
                <img
                  src={`${process.env.REACT_APP_MEDIA_URL}${influencer.image}`}
                  alt="Profile"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 rounded-full flex items-center justify-center">
                  <i className="bi bi-person text-gray-400 text-3xl"></i>
                </div>
              )}
            </div>
            <h4 className="font-bold text-lg">{influencer.first_name} {influencer.last_name}</h4>
            <p className="text-gray-500 mb-2">@{influencer.username || 'N/A'}</p>
            <div className="flex justify-center gap-2">
              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                influencer.verify === 'approved' ? 'bg-green-100 text-green-800' :
                influencer.verify === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {influencer.verify || 'Pending'}
              </span>
              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                influencer.emailVerified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                Email {influencer.emailVerified ? 'Verified' : 'Unverified'}
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('personal')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'personal'
                    ? 'border-[#009ef7] text-[#009ef7]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Personal
              </button>
              <button
                onClick={() => setActiveTab('social')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'social'
                    ? 'border-[#009ef7] text-[#009ef7]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Social Media
              </button>
              <button
                onClick={() => setActiveTab('skills')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'skills'
                    ? 'border-[#009ef7] text-[#009ef7]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Skills
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'personal' && (
              <div className="grid grid-cols-2 gap-4">
                <div><strong>Gender:</strong> {influencer.gender || '-'}</div>
                <div><strong>Travel Frequency:</strong> {influencer.travel_frequency || '-'}</div>
                <div><strong>Experience:</strong> {influencer.experience || '-'}</div>
                <div><strong>Engagement Rate:</strong> {influencer.engagement_rate || '-'}</div>
                <div className="col-span-2"><strong>About:</strong> {influencer.about || '-'}</div>
                <div className="col-span-2">
                  <strong>Portfolio:</strong> {
                    influencer.portfolio_url ? (
                      <a href={influencer.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline ml-2">
                        View Portfolio
                      </a>
                    ) : '-'
                  }
                </div>
              </div>
            )}

            {activeTab === 'social' && renderSocialMedia()}

            {activeTab === 'skills' && (
              <div className="space-y-4">
                <div>
                  <strong className="block mb-2">Services:</strong>
                  {influencer.services && influencer.services.length ? (
                    <div className="flex flex-wrap gap-1">
                      {influencer.services.map((service, index) => (
                        <span key={index} className="px-2 py-1 bg-[#009ef7]/10 text-[#009ef7] text-xs rounded">
                          {service}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500">No services</span>
                  )}
                </div>
                <div>
                  <strong className="block mb-2">Equipment:</strong>
                  {influencer.equipment && influencer.equipment.length ? (
                    <div className="flex flex-wrap gap-1">
                      {influencer.equipment.map((item, index) => (
                        <span key={index} className="px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded">
                          {item}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500">No equipment</span>
                  )}
                </div>
                <div>
                  <strong className="block mb-2">Languages:</strong>
                  {influencer.languages && influencer.languages.length ? (
                    <div className="flex flex-wrap gap-1">
                      {influencer.languages.map((lang, index) => (
                        <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                          {lang}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500">No languages</span>
                  )}
                </div>
                <div>
                  <strong className="block mb-2">Countries Visited:</strong>
                  {influencer.countryVisit && influencer.countryVisit.length ? (
                    <div className="flex flex-wrap gap-1">
                      {influencer.countryVisit.map((country, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                          {country}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500">No countries</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfluencerModal;